import React from 'react'
import { StatCards, Skeleton } from '@/shared/ui'
import { CreditCard, Layers, Users, Wallet } from 'lucide-react'
import { formatMoneyFromCents } from '@/shared/i18n/money'
import { getRevshareMasterMonthSummary, type RevshareMasterMonthSummary } from '../services/revshareSummaryService'
import { bpsToPercent } from '../utils/percent'

type Props = {
  monthStartDate: string
  refreshKey: number
}

export default function RevshareMasterSummaryTab({ monthStartDate, refreshKey }: Props) {
  const [summary, setSummary] = React.useState<RevshareMasterMonthSummary | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)

  async function load() {
    setLoading(true)
    setError(null)
    try {
      const s = await getRevshareMasterMonthSummary(monthStartDate)
      setSummary(s)
    } catch (e: any) {
      setError(e?.message || 'Falha ao carregar resumo')
      setSummary(null)
    } finally {
      setLoading(false)
    }
  }

  React.useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthStartDate, refreshKey])

  if (loading && !summary) {
    return (
      <StatCards
        items={Array.from({ length: 8 }).map(() => ({ label: '-', value: <Skeleton className="h-6 w-28" /> }))}
        className="lg:grid-cols-4"
      />
    )
  }

  if (error) return <div className="text-sm text-red-400">{error}</div>
  if (!summary) return <div className="text-sm text-foreground/70">Sem dados.</div>

  const cards = [
    {
      label: 'Receita liquida (mes)',
      value: formatMoneyFromCents(summary.revenue_net_cents, 'BRL'),
      icon: <CreditCard className="h-5 w-5" />,
      tone: 'primary' as const,
    },
    {
      label: 'Teto da pool (cap)',
      value: bpsToPercent(summary.pool_cap_bps, 2),
      icon: <Layers className="h-5 w-5" />,
      tone: 'violet' as const,
    },
    {
      label: 'Pool maxima (mes)',
      value: formatMoneyFromCents(summary.pool_cents, 'BRL'),
      icon: <Wallet className="h-5 w-5" />,
      tone: 'success' as const,
    },
    {
      label: 'Participantes ativos',
      value: String(summary.participants_active_count || 0),
      icon: <Users className="h-5 w-5" />,
      tone: 'primary' as const,
    },
    {
      label: 'Shares alocados (pool)',
      value: bpsToPercent(summary.shares_allocated_bps, 2),
      icon: <Layers className="h-5 w-5" />,
      tone: 'warning' as const,
    },
    {
      label: 'Total comissoes (mes)',
      value: formatMoneyFromCents(summary.total_commissions_cents, 'BRL'),
      icon: <Wallet className="h-5 w-5" />,
      tone: 'primary' as const,
    },
    {
      label: 'Disponivel (maturado)',
      value: formatMoneyFromCents(summary.total_available_cents, 'BRL'),
      icon: <Wallet className="h-5 w-5" />,
      tone: 'success' as const,
    },
    {
      label: 'Pago (mes)',
      value: formatMoneyFromCents(summary.total_paid_cents, 'BRL'),
      icon: <Wallet className="h-5 w-5" />,
      tone: 'success' as const,
    },
  ]

  return <StatCards items={cards} className="lg:grid-cols-4" />
}

